import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, User, ArrowRight, Eye, Code, Globe } from 'lucide-react';
import { usePortfolio } from '../hooks/useAPI';

const PortfolioSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [filter, setFilter] = useState('all');
  const { data: portfolio, isLoading, error } = usePortfolio();

  // Default portfolio items if API fails
  const defaultPortfolio = [
    {
      id: 1,
      title: 'E-Commerce Platform Modernization',
      description: 'Complete overhaul of legacy e-commerce system, resulting in 300% performance improvement and mobile-first design.',
      category: 'Web Development',
      client: 'RetailTech Solutions',
      completedAt: '2024-01-15',
      technologies: ['React', 'Node.js', 'MongoDB', 'AWS'],
      liveUrl: '#',
      caseStudyUrl: '#',
      image: null
    },
    {
      id: 2,
      title: 'AI-Powered Inventory Management',
      description: 'Machine learning solution that predicts inventory needs and automates ordering processes for manufacturing company.',
      category: 'AI/ML',
      client: 'Manufacturing Pro',
      completedAt: '2024-02-20',
      technologies: ['Python', 'TensorFlow', 'PostgreSQL', 'Docker'],
      liveUrl: null,
      caseStudyUrl: '#',
      image: null
    },
    {
      id: 3,
      title: 'Mobile Banking Application',
      description: 'Secure, user-friendly mobile banking app with biometric authentication and real-time transaction monitoring.',
      category: 'Mobile Development',
      client: 'FinanceFirst Bank',
      completedAt: '2024-03-10',
      technologies: ['React Native', 'Node.js', 'PostgreSQL', 'AWS'],
      liveUrl: '#',
      caseStudyUrl: '#',
      image: null
    },
    {
      id: 4,
      title: 'Corporate Security Audit Platform',
      description: 'Comprehensive cybersecurity assessment tool that identifies vulnerabilities and provides actionable recommendations.',
      category: 'Cybersecurity',
      client: 'SecureGuard Enterprise',
      completedAt: '2023-12-05',
      technologies: ['Vue.js', 'Express.js', 'MySQL', 'Docker'],
      liveUrl: null,
      caseStudyUrl: '#',
      image: null
    },
    {
      id: 5,
      title: 'Digital Marketing Analytics Dashboard',
      description: 'Real-time marketing performance dashboard with advanced analytics and automated reporting capabilities.',
      category: 'Data Analytics',
      client: 'MarketGrow Agency',
      completedAt: '2024-01-30',
      technologies: ['Angular', 'Python', 'MongoDB', 'Tableau'],
      liveUrl: '#',
      caseStudyUrl: '#',
      image: null
    },
    {
      id: 6,
      title: 'Enterprise Resource Planning System',
      description: 'Custom ERP solution integrating all business processes with advanced reporting and workflow automation.',
      category: 'Enterprise Software',
      client: 'GlobalTech Industries',
      completedAt: '2023-11-15',
      technologies: ['React', 'Java Spring', 'Oracle', 'Kubernetes'],
      liveUrl: null,
      caseStudyUrl: '#',
      image: null
    }
  ];

  const displayPortfolio = portfolio && portfolio.length > 0 ? portfolio : defaultPortfolio;
  const categories = ['all', ...new Set(displayPortfolio.map(item => item.category))];
  
  const filteredPortfolio = filter === 'all' 
    ? displayPortfolio 
    : displayPortfolio.filter(item => item.category === filter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  return (
    <section id="portfolio" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR PORTFOLIO
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Success Stories & Case Studies
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Explore our portfolio of successful projects that showcase our expertise across various industries and technologies.
          </motion.p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                filter === category
                  ? 'bg-lion-yellow text-lion-blue shadow-md'
                  : 'bg-lion-gray text-gray-600 hover:bg-gray-300'
              }`}
            >
              {category === 'all' ? 'All Projects' : category}
            </button>
          ))}
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredPortfolio.map((item, index) => (
              <motion.div
                key={item.id || index}
                variants={cardVariants}
                custom={index}
                className="bg-lion-gray rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                {/* Project Image */}
                <div className="h-48 bg-gradient-to-br from-lion-blue to-lion-yellow flex items-center justify-center relative overflow-hidden">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="text-center text-white p-6">
                      <Code className="w-12 h-12 mx-auto mb-2 opacity-80" />
                      <span className="text-sm font-medium opacity-90">{item.category}</span>
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                </div>
                
                <div className="p-6">
                  {/* Category Badge */}
                  <span className="inline-block bg-lion-yellow text-lion-blue text-xs font-medium px-3 py-1 rounded-full mb-3">
                    {item.category}
                  </span>
                  
                  <h3 className="text-xl font-poppins font-bold text-lion-blue mb-3 group-hover:text-lion-yellow transition-colors duration-300">
                    {item.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Client and Date */}
                  <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {item.client}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(item.completedAt)}
                    </div>
                  </div>
                  
                  {/* Technologies */}
                  {item.technologies && item.technologies.length > 0 && (
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2">
                        {item.technologies.slice(0, 4).map((tech, idx) => (
                          <span
                            key={idx}
                            className="bg-white text-gray-600 text-xs px-2 py-1 rounded border"
                          >
                            {tech}
                          </span>
                        ))}
                        {item.technologies.length > 4 && (
                          <span className="text-xs text-gray-500">
                            +{item.technologies.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {item.liveUrl && (
                        <a
                          href={item.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-lion-blue hover:text-lion-yellow transition-colors duration-300 text-sm"
                        >
                          <Globe className="w-4 h-4 mr-1" />
                          Live Site
                        </a>
                      )}
                      
                      {item.caseStudyUrl && (
                        <a
                          href={item.caseStudyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-lion-blue hover:text-lion-yellow transition-colors duration-300 text-sm"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Case Study
                        </a>
                      )}
                    </div>
                    
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-lion-yellow group-hover:transform group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {error && (
          <motion.p 
            variants={itemVariants}
            className="text-gray-500 text-center mt-8"
          >
            Portfolio items will be loaded shortly. Please check back soon.
          </motion.p>
        )}

        {/* Call to Action */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-poppins font-bold text-lion-blue mb-4">
            Ready to Start Your Project?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Let's discuss how we can help bring your vision to life with innovative technology solutions.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center bg-lion-yellow text-lion-blue px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transform hover:scale-105 transition-all duration-300"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;